import React from 'react';
import { motion } from 'framer-motion';
import LandingCarousel from '../component/core/HomePage/LandingCarousels/LandingCarousel';
import { Link } from 'react-router-dom';
import Navbar from '../component/common/Navbar';
import { Gamepad2, Users, Calendar, Trophy, MapPin, Star, Zap, Target } from 'lucide-react';

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2
    }
  }
};

const About = () => {
  return (
    <div className="min-h-screen text-white bg-black overflow-hidden">

      <Navbar />

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-black rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-black rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      {/* Hero Section */}
      <motion.section 
        className="relative pt-20 pb-16"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
      >
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="mb-8">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-black to-yellow-500 px-6 py-3 rounded-full text-sm font-semibold mb-6">
              <Gamepad2 className="w-5 h-5" />
              <span>🎮 PlayGG: Empowering Gaming Cafes Across India 🚀</span>
            </div>
          </div>

          <h1 className="text-3xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-black via-yellow-400 to-yellow-600 leading-tight">
            Empowering Gaming Cafes
            <br />
            <span className="text-2xl md:text-4xl">Across India</span>
          </h1>

          <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
            At PlayGG, we are dedicated to transforming the gaming cafe landscape in India. Our innovative platform provides gaming cafes with comprehensive tools to manage bookings, promote events, and reach a wider audience, driving business growth and enhancing the gaming experience for their customers.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/explore" 
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-black to-yellow-500 hover:from-black hover:to-yellow-600 rounded-full transition-all duration-300 text-lg font-semibold transform hover:scale-105 shadow-lg"
            >
              <MapPin className="w-5 h-5" />
              Explore Cafes
            </Link>
            
            
          </div>
        </div>
      </motion.section>

      {/* What We Offer Section */}
      <motion.section 
        className="relative py-20 px-4 md:px-8 bg-black/20 backdrop-blur-sm"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-6xl mx-auto">
          <motion.div variants={fadeUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-black to-yellow-500">
              🌟 What We Offer
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-black to-yellow-500 mx-auto rounded-full"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div variants={fadeUp}><FeatureCard title="Real-Time Cafe Availability" description="Gaming cafes can seamlessly manage and display their real-time availability, making it easier for gamers to book spots and plan their gaming sessions." icon={<Calendar className="w-8 h-8" />} gradient="from-black to-yellow-500" /></motion.div>
            <motion.div variants={fadeUp}><FeatureCard title="Efficient Booking Management" description="Our platform streamlines the booking process, allowing cafes to efficiently handle reservations and reduce no-shows." icon={<Target className="w-8 h-8" />} gradient="from-black to-yellow-500" /></motion.div>
            <motion.div variants={fadeUp}><FeatureCard title="Event Promotion" description="Gaming cafes can easily promote their tournaments, leagues, and special events through our platform, attracting more participants and boosting engagement." icon={<Trophy className="w-8 h-8" />} gradient="from-black to-yellow-500" /></motion.div>
            <motion.div variants={fadeUp}><FeatureCard title="Customer Reach" description="By leveraging PlayGG, cafes can expand their reach and connect with a broader audience of passionate gamers." icon={<Users className="w-8 h-8" />} gradient="from-black to-yellow-500" /></motion.div>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

const FeatureCard = ({ title, description, icon, gradient }) => {
  return (
    <motion.div 
      whileHover={{ scale: 1.05 }}
      className="group p-10 rounded-2xl border border-gray-700 hover:border-yellow-500 transition-all duration-300 bg-gray-800/30 backdrop-blur-sm h-96 flex flex-col justify-between"
    >
      <div className="flex-grow">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${gradient} mb-6 flex-shrink-0`}>
          <div className="text-white">
            {icon}
          </div>
        </div>
        <h3 className="text-lg font-bold mb-4 text-white group-hover:text-yellow-400 transition-colors duration-300">
          {title}
        </h3>
        <p className="text-sm text-gray-400 leading-relaxed overflow-hidden text-start">
          {description}
        </p>
      </div>
    </motion.div>
  );
};

export default About;
